/* ===========================================================
   shared/render.js — doc JSON → read-only HTML

   For places that show a doc without an editor: Compare, conflict
   choices, version previews. Every bit of text and every attribute is
   escaped; links only keep http(s)/mailto, images only our own
   /f/<doc>/<id> files, https or data:image. Nodes marked by diff.js (__d) get
   .d-ins / .d-del / .d-mod classes.
   =========================================================== */

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ESC[c]);

export function safeHref(href) {
  const h = String(href || '').trim();
  return /^(https?:|mailto:)/i.test(h) ? h : '';
}
export function safeSrc(src) {
  const s = String(src || '').trim();
  return /^(\/f\/[\w-]+\/[\w-]+$|https:\/\/|data:image\/(png|jpe?g|gif|webp);)/i.test(s) ? s : '';
}

const dcls = n => (n.__d === 'ins' ? ' class="d-ins"' : n.__d === 'del' ? ' class="d-del"' : n.__d === 'mod' ? ' class="d-mod"' : '');

/**
 * @param {object} node  doc or node JSON
 * @param {{ math?: (latex: string, display: boolean) => string }} opts
 */
export function renderHTML(node, opts = {}) {
  return render(node, opts);
}

function render(n, o) {
  if (!n) return '';
  const kids = () => (n.content || []).map(k => render(k, o)).join('');
  const a = n.attrs || {};
  const align = a.textAlign && a.textAlign !== 'left' ? ` style="text-align:${esc(a.textAlign)}"` : '';
  switch (n.type) {
    case 'doc': return kids();
    case 'text': return textHTML(n);
    case 'paragraph': return `<p${dcls(n)}${align}>${kids() || '<br>'}</p>`;
    case 'heading': {
      const l = Math.min(6, Math.max(1, Number(a.level) || 1));
      return `<h${l}${dcls(n)}${align}>${kids()}</h${l}>`;
    }
    case 'bulletList': return `<ul${dcls(n)}>${kids()}</ul>`;
    case 'orderedList': return `<ol${dcls(n)}${a.start > 1 ? ` start="${Number(a.start)}"` : ''}>${kids()}</ol>`;
    case 'listItem': return `<li${dcls(n)}>${kids()}</li>`;
    case 'taskList': return `<ul class="tasks${n.__d ? ` d-${n.__d}` : ''}">${kids()}</ul>`;
    case 'taskItem': return `<li class="task${a.checked ? ' done' : ''}${n.__d ? ` d-${n.__d}` : ''}"><span class="box" aria-hidden="true">${a.checked ? '✓' : ''}</span><div>${kids()}</div></li>`;
    case 'blockquote': return `<blockquote${dcls(n)}>${kids()}</blockquote>`;
    case 'horizontalRule': return `<hr${dcls(n)}>`;
    case 'hardBreak': return '<br>';
    case 'codeBlock': return `<pre${dcls(n)}${a.language ? ` data-lang="${esc(a.language)}"` : ''}><code>${kids()}</code></pre>`;
    case 'image': {
      const src = safeSrc(a.src);
      return src ? `<img${dcls(n)} src="${esc(src)}" alt="${esc(a.alt || '')}" loading="lazy">` : `<span class="missing-img">[image]</span>`;
    }
    case 'table': return `<table${dcls(n)}><tbody>${kids()}</tbody></table>`;
    case 'tableRow': return `<tr${dcls(n)}>${kids()}</tr>`;
    case 'tableHeader': return `<th${dcls(n)}${span(a)}>${kids()}</th>`;
    case 'tableCell': return `<td${dcls(n)}${span(a)}>${kids()}</td>`;
    case 'mathInline': return `<span class="math-inline${n.__d ? ` d-${n.__d}` : ''}">${o.math ? o.math(a.latex || '', false) : `<code>${esc(a.latex)}</code>`}</span>`;
    case 'mathBlock': return `<div class="math-block${n.__d ? ` d-${n.__d}` : ''}">${o.math ? o.math(a.latex || '', true) : `<code>${esc(a.latex)}</code>`}</div>`;
    case 'sandbox': return `<div class="sandbox-static${n.__d ? ` d-${n.__d}` : ''}">${kids()}</div>`;
    case 'sandboxCode': return `<pre${dcls(n)} data-lang="${esc(a.lang || '')}"><code>${kids()}</code></pre>`;
    default: return kids();
  }
}

const span = a => `${a.colspan > 1 ? ` colspan="${Number(a.colspan)}"` : ''}${a.rowspan > 1 ? ` rowspan="${Number(a.rowspan)}"` : ''}`;

function textHTML(n) {
  let html = esc(n.text).replace(/\n/g, '<br>');
  for (const m of n.marks || []) {
    switch (m.type) {
      case 'bold': html = `<strong>${html}</strong>`; break;
      case 'italic': html = `<em>${html}</em>`; break;
      case 'underline': html = `<u>${html}</u>`; break;
      case 'strike': html = `<s>${html}</s>`; break;
      case 'code': html = `<code>${html}</code>`; break;
      case 'highlight': html = `<mark>${html}</mark>`; break;
      case 'link': {
        const href = safeHref(m.attrs?.href);
        if (href) html = `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer nofollow">${html}</a>`;
        break;
      }
      default: break;
    }
  }
  if (n.__d === 'ins') html = `<ins>${html}</ins>`;
  else if (n.__d === 'del') html = `<del>${html}</del>`;
  return html;
}
